import React, { useState, useRef, useEffect } from 'react';
import { useCourseAccess } from '../hooks/useCourseAccess';
import { CourseAccessGuard } from './CourseAccessGuard';
import { getBunnyNetEmbedUrl, getBunnyNetDirectUrl, BUNNY_NET_CONFIG } from '../config/bunnyNet';

interface VideoPlayerProps {
  moduleNumber: number;
  videoId: string;
  title: string;
  description?: string;
  bunnyNetVideoId?: string;
  posterUrl?: string;
  libraryId?: string;
}

export const VideoPlayer: React.FC<VideoPlayerProps> = ({
  moduleNumber,
  videoId,
  title,
  description,
  bunnyNetVideoId,
  posterUrl,
  libraryId
}) => {
  const { hasModuleAccess, getAccessLevelName } = useCourseAccess();
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Construct bunny.net video URL
  const getBunnyNetVideoUrl = () => {
    if (bunnyNetVideoId) {
      return getBunnyNetEmbedUrl(bunnyNetVideoId, libraryId);
    }
    // Fallback to direct video URL if you have the direct path
    return getBunnyNetDirectUrl(videoId, libraryId);
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <CourseAccessGuard requiredModule={moduleNumber}>
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="relative">
          {/* Video Player */}
          <div className="relative bg-black">
            {bunnyNetVideoId ? (
              // Use bunny.net iframe embed
              <iframe
                src={getBunnyNetVideoUrl()}
                loading="lazy"
                style={{ border: 'none' }}
                title={title}
                allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                allowFullScreen
                className="w-full aspect-video"
              />
            ) : (
              // Use HTML5 video player
              <video
                ref={videoRef}
                className="w-full aspect-video"
                poster={posterUrl}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                {...BUNNY_NET_CONFIG.PLAYER_SETTINGS}
              >
                <source src={getBunnyNetVideoUrl()} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            )}
          </div>

          {/* Video Info */}
          <div className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold text-[#203D32] mb-2">
                  {title}
                </h3>
                {description && (
                  <p className="text-gray-600 text-sm">{description}</p>
                )}
              </div>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                Module {moduleNumber}
              </span>
            </div>

            {/* Progress Bar (for HTML5 video) */}
            {!bunnyNetVideoId && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm text-gray-500">
                  <span>{formatTime(currentTime)}</span>
                  <span>{formatTime(duration)}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-[#203D32] h-2 rounded-full transition-all duration-300"
                    style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
                  />
                </div>
              </div>
            )}

            {/* Access Level Info */}
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700">
                <strong>Access Level:</strong> {getAccessLevelName()}
              </p>
              <p className="text-xs text-blue-600 mt-1">
                You have access to this module with your current subscription.
              </p>
            </div>
          </div>
        </div>
      </div>
    </CourseAccessGuard>
  );
}; 